import servicesConfig, { CustomAxiosHeaders } from '../config/services'

import { AxiosResponse } from 'axios'
import config from './../config/index'
import { requests } from '.'

// Configure request setup
const serviceName = 'WhatsAppService'
const host = config.ghlRevexBackendUrl + '/whatsapp'
const opts: CustomAxiosHeaders = { host, serviceName }
const request = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, opts, addlOpts))
}

// Helper function to handle response
const responseBody = (res: AxiosResponse) => res.data

// Define service object with methods
const WhatsAppService = {
  isConfigured: (locationId: string) => request().get(`/location/${locationId}/is-configured`).then(responseBody),

  companyHasWallet: (companyId: string) => request().get(`/company/${companyId}/has-wallet`).then(responseBody),

  resync: (data: { locationId: string }) => request().put(`/location/${data.locationId}/resync`, {}).then(responseBody),

  delete: (data: { locationId: string }) =>
    request().delete(`/location/${data.locationId}/delete`, {}).then(responseBody),

  disconnect: (data: { locationId: string }) =>
    request().delete(`/location/${data.locationId}/disconnect`, {}).then(responseBody),

  getAccountInfo: (data: { locationId: string }) =>
    request().get(`/location/${data.locationId}/account-info`, {}).then(responseBody),

  getWhatsAppPhoneNumbersForLocationId: (locationId: string) =>
    request().get(`/location/${locationId}/phone-numbers`).then(responseBody),

  makeDefaultPhoneNumber: (locationId: string, phoneNumberId: string) =>
    request().put(`/location/${locationId}/phone-numbers/${phoneNumberId}/default`, {}).then(responseBody),

  getWhatsAppTemplatesForLocationId: (locationId: string, templateId: string, includeRewriterIds: boolean) => {
    let url = `/location/${locationId}/template`

    if (templateId) {
      url += `?templateId=${templateId}`
    }
    if (includeRewriterIds) {
      url += '?includeRewriterIds=true'
    }

    return request().get(url).then(responseBody)
  },

  createWhatsAppTemplateForLocationId: (locationId: string, data: any) =>
    request().post(`/location/${locationId}/template`, data).then(responseBody),

  updateWhatsAppTemplateForLocationId: (locationId: string, templateId: string, data: any) =>
    request().put(`/location/${locationId}/template/${templateId}`, data).then(responseBody),

  deleteWhatsAppTemplateForLocationId: (locationId: string, templateId: string, templateName: string) =>
    request().delete(`/location/${locationId}/template/${templateId}/${templateName}`).then(responseBody),

  getTemplateFolders: (locationId: string, folderId?: string) => {
    const url = folderId ? `/location/${locationId}/folders?folderId=${folderId}` : `/location/${locationId}/folders`
    return request().get(url).then(responseBody)
  },
  createTemplateFolder: (locationId: string, data: any) =>
    request().post(`/location/${locationId}/folders`, data).then(responseBody),

  updateTemplateFolder: (locationId: string, data: any) => {
    if (data.folderId) {
      const url = `/location/${locationId}/folders/${data.folderId}`
      delete data.folderId
      return request().put(url, data).then(responseBody)
    } else {
      console.log('data.folderId is not present')
    }
  },
  deleteTemplateFolder: (locationId: string, folderId: string, parentFolderId?: string) => {
    try {
      return request()
        .delete(`/location/${locationId}/folders/${folderId}?parentFolderId=${parentFolderId}`)
        .then(responseBody)
    } catch (error) {
      return error
    }
  },
  addTemplateToFolder: (locationId: string, data: any) => {
    return request().post(`/location/${locationId}/folders/map-template`, data).then(responseBody)
  },

  getProduct: (locationId?: string) => request().get(`/reselling/product`).then(responseBody),

  getCompanyResellingConfiguration: (companyId: string) =>
    request().get(`/company/${companyId}/reselling/configuration`).then(responseBody),

  updateCompanyResellingConfiguration: (companyId: string, data: { amount: number }) =>
    request().put(`/company/${companyId}/reselling/configuration`, data).then(responseBody),

  getLocationResellingConfiguration: (locationId: string) =>
    request().get(`/location/${locationId}/reselling/configuration`).then(responseBody),

  updateLocationResellingConfiguration: (locationId: string, data: { amount?: number; offeringEnabled?: boolean }) =>
    request().put(`/location/${locationId}/reselling/configuration`, data).then(responseBody),

  getCompanySubscriptions: (companyId: string) =>
    request().get(`/company/${companyId}/reselling/subscription`).then(responseBody),

  getLocationSubscriptions: (locationId: string) =>
    request().get(`/location/${locationId}/reselling/subscription`).then(responseBody),

  cancelSubscription: (locationId: string, reason: string, disconnectWaba = false) => {
    return request()
      .delete(`/location/${locationId}/cancelSubscription`, {
        data: { reason, disconnectWaba },
      })
      .then(responseBody)
  },

  locationSub: (locationId: string, data: any) =>
    request().post(`/location/${locationId}/reselling/subscription`, data).then(responseBody),

  getMediaUrlInfo: (url: string, locationId: string) =>
    request().get(`/location/${locationId}/media?url=${url}`).then(responseBody),

  getAttachement: (locationId: string, messageId: string) =>
    request()
      .get(`/location/${locationId}/messages/${messageId}/attachment`, {
        responseType: 'blob',
      })
      .then(responseBody),

  postAttachement: (locationId: string, conversationId: string, data: any) =>
    request().post(`/location/${locationId}/attachment?conversationId=${conversationId}`, data).then(responseBody),

  voiceNote: (params: any, data: any) =>
    request()
      .post(
        `/location/${params.locationId}/voice-note?userId=${params.userId}&contactId=${params.contactId}&conversationId=${params.conversationId}`,
        data
      )
      .then(responseBody),

  upload: (locationId: string, data: { formData: any }) => {
    const url = `/location/${locationId}/uploads`
    return request().post(url, data.formData).then(responseBody)
  },

  getSignedUrl: (locationId: string, data: any) => {
    const url = `/location/${locationId}/media-signed-url`
    return request().post(url, data).then(responseBody)
  },

  enableWhatsAppConversationRebilling: (companyId: string, locationId: string) =>
    request().post(`/company/${companyId}/location/${locationId}/rebilling/enable`, {}).then(responseBody),

  getWhatsappBusinessProfileData: (data: { phoneNumberId: string; wabaId: string; locationId: string }) =>
    request()
      .get(`/location/${data.locationId}/businessprofile/${data.wabaId}/${data.phoneNumberId}`)
      .then(responseBody),

  postWhatsappBusinessProfileData: (data: any) => {
    const phoneNumberId = data.phoneNumberId
    const wabaId = data.wabaId
    const locationId = data.locationId
    delete data.wabaId
    delete data.phoneNumberId
    delete data.locationId
    return request().post(`/location/${locationId}/businessprofile/${wabaId}/${phoneNumberId}`, data).then(responseBody)
  },

  addPhoneNumber: (locationId: string, data: any) => {
    return request().post(`/phone-numbers?locationId=${locationId}`, data).then(responseBody)
  },

  sendOtp: (data: any) => {
    return request().post(`/location/${data.locationId}/phone-numbers/send-otp`, data).then(responseBody)
  },

  verifyOtp: (data: any) => {
    return request().post(`/location/${data.locationId}/phone-numbers/verify-otp`, data).then(responseBody)
  },

  getAnalytics: (data: {
    startDate: string
    endDate: string
    wabaId: string
    templateId: string
    templateIds: string[]
    locationId: string
  }) => {
    const templateIdsParam = data.templateIds.map(id => `templateIds[]=${id}`).join('&')
    const url = `/location/${data.locationId}/analytics?startDate=${data.startDate}&endDate=${data.endDate}&wabaId=${data.wabaId}&${templateIdsParam}`
    return request().get(url).then(responseBody)
  },

  register: (data: any, locationId: string) => {
    return request().post(`/location/${locationId}/phone-numbers/register`, data).then(responseBody)
  },

  setupFlows: (locationId: string) => {
    return request().post(`/flows/setup?locationId=${locationId}`, {}).then(responseBody)
  },

  getFlows: (locationId: string) => {
    return request().get(`/flows?locationId=${locationId}`).then(responseBody)
  },

  createFlowToken: (data: any) => {
    return request().post(`/flows/token`, data).then(responseBody)
  },

  createFlow: (data: { wabaId: string; body: any }) => {
    return request().post(`/flows?whatsAppBusinessAccountId=${data.wabaId}`, data.body).then(responseBody)
  },

  updateFlow: (data: { flowId: string; locationId: string; body: any; wabaId: string }) => {
    return request()
      .put(`/flows/${data.flowId}?locationId=${data.locationId}&whatsAppBusinessAccountId=${data.wabaId}`, data.body)
      .then(responseBody)
  },

  updateFlowAssets: (data: { flowId: string; body: any; locationId?: string }) => {
    const locationId = data.locationId
    delete data?.locationId
    return request().put(`/flows/location/${locationId}/${data.flowId}/assets`, data.body).then(responseBody)
  },

  flowPreview: (data: { flowId: string; locationId: string }) => {
    return request().get(`/flows/location/${data.locationId}/${data.flowId}/preview`).then(responseBody)
  },

  flowPublish: (data: { flowId: string; locationId: string; wabaId: string }) => {
    return request()
      .post(`/flows/${data.flowId}/publish?locationId=${data.locationId}&whatsAppBusinessAccountId=${data.wabaId}`, {})
      .then(responseBody)
  },

  deprecateFlow: (data: { id: string; locationId: string; wabaId: string }) => {
    return request()
      .post(`/flows/${data.id}/deprecate?locationId=${data.locationId}&whatsAppBusinessAccountId=${data.wabaId}`, {})
      .then(responseBody)
  },

  deleteFlow: (data: { id: string; locationId: string; wabaId: string }) => {
    return request()
      .delete(`/flows/${data.id}/delete?locationId=${data.locationId}&whatsAppBusinessAccountId=${data.wabaId}`, {})
      .then(responseBody)
  },

  getOnboardingChecklist: (locationId: string) => {
    return request().get(`/onboarding-checklist/${locationId}`).then(responseBody)
  },

  templateRewriter: (data: any) => {
    return request().put(`/ai/template-rewriter`, data).then(responseBody)
  },

  getCountdownLinks: (params?: any) => {
    return request().get(`/links/countdown-links?locationId=${params.locationId}`).then(responseBody)
  },
  getCountdownLink: (params?: any) => {
    return request()
      .get(`/links/countdown-link?locationId=${params.locationId}&linkId=${params.linkId}`)
      .then(responseBody)
  },
  getMessagingAnalytics: (params: { locationId: string; startDate: string; endDate: string }) => {
    return request()
      .get(
        `/message/analytics/messaging?locationId=${params.locationId}&startDate=${params.startDate}&endDate=${params.endDate}`
      )
      .then(responseBody)
  },
}

export const whatsAppService = WhatsAppService

export default {}
